import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button, LoginPrompt } from 'src/components';
import CommentItem from './comment-item';
import CommentForm from './comment-form';
import { getComments, useAuth } from 'src/utils';
import { useIsMobile } from 'src/hooks';

export default function CommentsContainer({ articleId }) {
    const { user } = useAuth();
    const isMobile = useIsMobile();

    const [comments, setComments] = useState([]);
    const [showAll, setShowAll] = useState(comments.length === 0 || comments.length === 1 ? true : false);
    const [loading, setLoading] = useState(user ? true : false);
    const [errorMsg, setErrorMsg] = useState("");
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        const fetchComments = async () => {
            try {
                setLoading(true);
                setErrorMsg("");
                const res = await getComments(articleId);
                setComments(res);
            } catch (err) {
                setErrorMsg(err.message || 'Error fetching comments. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        
        if (user && articleId) {
            fetchComments();
        }
    }, [articleId, user]);

    const getSortedComments = (comments) => {
        const topLevel = comments.filter(c => !c.parentCommentId);
        
        switch (sortBy) {
            case 'oldest':
                return topLevel.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
            case 'popular':
                return topLevel.sort((a, b) => (b.upvoters?.length || 0) - (a.upvoters?.length || 0));
            case 'newest':
            default:
                return topLevel.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
    };

    const handleNewComment  = (newComment) => {
        if (!user) return; 
        const enrichedComment = {
            ...newComment,
            userDisplayName: user.name,
            userAvatarUrl: user.profileImageUrl,
        };
        setComments(prev => [enrichedComment, ...prev]);
    };

    const topLevelComments = getSortedComments(comments);
    const visibleComments = showAll ? topLevelComments : topLevelComments.slice(0, 1);



    return (
        <Box mt={4} px={isMobile ? 1 : 10}>
        {/* Header */}
        <Box mb={3}>
            <Typography variant="h3" fontWeight={600} gutterBottom>
                Comments ({topLevelComments.length})
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant="body1" color="text.secondary">
                    {comments.length === 0 
                        ? "Be the first to share your thoughts"
                        : "Join the conversation"
                    }
                </Typography>
                {user && comments.length !== 0 && (
                    <Button
                        disableRipple
                        variant="text"
                        size="small"
                        onClick={() => {
                            const options = ['newest', 'oldest', 'popular'];
                            const current = options.indexOf(sortBy);
                            const next = (current + 1) % options.length;
                            setSortBy(options[next]);
                        }}
                        sx={{ 
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            color: 'text.secondary'
                        }}
                    >
                        Sort: {sortBy === 'newest' ? 'Newest' : sortBy === 'oldest' ? 'Oldest' : 'Most Popular'}
                    </Button>
                )}
            </Box>
        </Box>

        {loading && (
            <Box sx={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                <CircularProgress />
            </Box>
        )}

        {/* Login Required State */}
        {!user && (
            <LoginPrompt 
                message={"Log in to read and post comments."}
                height={"150px"} />
        )}

        {/* Comments List */}
        {user && (
            <Box>

            {/* Comments */}
            <Box>
                {visibleComments.map(comment => (
                <CommentItem
                    key={comment.id}
                    comment={comment}
                    allComments={comments}
                    currentUser={user}
                    onCommentUpdate={setComments}
                    depth={0}
                    initialRepliesCollapsed={!showAll} 
                />
                ))}

                {/* Load More Button */}
                {topLevelComments.length > 1 && !showAll && (
                <Box textAlign="center" mb={2}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => setShowAll(true)}
                        sx={{ 
                            borderRadius: 100,
                            fontWeight: 600
                        }}
                        >
                        Show {topLevelComments.length - 1} more comments
                    </Button>
                </Box>
                )}

                {/* Empty State */}
                {(topLevelComments.length === 0 || errorMsg) && (
                <Box 
                    py={4}
                    px={2}
                    mb={4} 
                    textAlign="center"
                    sx={{ 
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {errorMsg ? (
                        <Typography variant="body1" color="error">
                            {errorMsg}
                        </Typography>
                    ):
                        <Typography variant="body1" color="text.secondary">
                            No comments yet. Be the first to share your thoughts!
                        </Typography>
                    }
                </Box>
                )}

                {/* Comment Form */}
                <Box mb={3} sx={{width: isMobile ? '100%' : '70%', margin: 'auto'}}>
                    <CommentForm
                        articleId={articleId}
                        onSuccess={newComment => handleNewComment(newComment)}
                    />
                </Box>

            </Box>
            </Box>
        )}

        </Box>
    );
}